import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  Plus,
  Save,
  ShieldCheck,
  Trash2,
  XCircle,
} from "lucide-react";
import { api, toastError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// constants (mirror backend agent_config.py)
// ---------------------------------------------------------------------------
const LANGUAGES = [
  { code: "en", flag: "🇬🇧", label: "English" },
  { code: "it", flag: "🇮🇹", label: "Italian" },
  { code: "es", flag: "🇪🇸", label: "Spanish" },
  { code: "de", flag: "🇩🇪", label: "German" },
];

const STT_MODELS = {
  deepgram: ["nova-3", "nova-2"],
  assemblyai: ["best", "nano"],
};
const LLM_MODELS = {
  groq: ["llama-3.3-70b-versatile", "llama-3.1-8b-instant"],
  openai: ["gpt-4o-mini", "gpt-4o"],
};
const TTS_MODELS = {
  cartesia: ["sonic-3", "sonic-2"],
  elevenlabs: ["eleven_flash_v2_5", "eleven_multilingual_v2"],
};

const DEFAULTS = {
  name: "",
  description: "",
  language: "en",
  system_prompt: "",
  first_message: "",
  end_rules: [],
  qa_pairs: [],
  stt_provider: "deepgram",
  stt_model: "",
  llm_provider: "groq",
  llm_model: "llama-3.3-70b-versatile",
  llm_temperature: 0.4,
  llm_max_tokens: 200,
  tts_provider: "cartesia",
  tts_model: "sonic-3",
  tts_voice_id: "",
  max_call_duration_s: 600,
  silence_timeout_s: 30,
  allow_barge_in: true,
  enabled: true,
};

// ---------------------------------------------------------------------------
export default function AgentDesigner() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [initial, setInitial] = useState(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  const form = useForm({ defaultValues: DEFAULTS });
  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = form;

  const language = watch("language");
  const sttProvider = watch("stt_provider");
  const llmProvider = watch("llm_provider");
  const ttsProvider = watch("tts_provider");

  // load existing agent
  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const res = await api.get(`/agents/${id}`);
        const data = { ...DEFAULTS, ...res.data };
        // ensure first_message + description are strings (not null)
        data.description = data.description || "";
        data.first_message = data.first_message || "";
        data.stt_model = data.stt_model || "";
        data.llm_model = data.llm_model || LLM_MODELS[data.llm_provider]?.[0] || "";
        data.tts_model = data.tts_model || TTS_MODELS[data.tts_provider]?.[0] || "";
        reset(data);
        setInitial(data);
      } catch (err) {
        toastError(err, "Failed to load agent");
        navigate("/agents");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // unsaved-changes guard for browser close / refresh.
  // (In-app nav uses a confirm() in the explicit back button.)
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const goBack = () => {
    if (isDirty && !window.confirm("You have unsaved changes. Leave anyway?")) {
      return;
    }
    navigate("/agents");
  };

  const onSubmit = async (values) => {
    // Strip empty optional strings → null/undefined so backend defaults apply
    const payload = { ...values };
    if (!payload.first_message) payload.first_message = null;
    if (!payload.description) payload.description = null;
    if (!payload.stt_model) payload.stt_model = null;
    if (!payload.llm_model) payload.llm_model = null;
    if (!payload.tts_model) payload.tts_model = null;
    payload.llm_temperature = Number(payload.llm_temperature);
    payload.llm_max_tokens = Number(payload.llm_max_tokens);
    payload.max_call_duration_s = Number(payload.max_call_duration_s);
    payload.silence_timeout_s = Number(payload.silence_timeout_s);
    // Strip server-controlled fields the form may carry over from a load
    delete payload.id;
    delete payload.created_at;
    delete payload.updated_at;

    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/agents/${id}`, payload);
        toast.success("Agent updated");
      } else {
        const res = await api.post("/agents", payload);
        toast.success("Agent created");
        navigate(`/agents/${res.data.id}`, { replace: true });
        return;
      }
      // Refresh "initial" so isDirty becomes false
      const res = await api.get(`/agents/${id}`);
      const data = { ...DEFAULTS, ...res.data };
      reset(data);
      setInitial(data);
    } catch (err) {
      toastError(err, "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground" data-testid="designer-loading">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading agent…
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="pb-24"
      data-testid="agent-designer"
    >
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={goBack}
          data-testid="designer-back"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {isEdit ? initial?.name || "Agent" : "New agent"}
          </h1>
          {isDirty && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Unsaved changes
            </p>
          )}
        </div>
      </div>

      <Tabs defaultValue="basics">
        <TabsList data-testid="designer-tabs">
          <TabsTrigger value="basics" data-testid="tab-basics">Basics</TabsTrigger>
          <TabsTrigger value="behaviour" data-testid="tab-behaviour">Behaviour</TabsTrigger>
          <TabsTrigger value="voice" data-testid="tab-voice">Voice & AI</TabsTrigger>
          <TabsTrigger value="lifecycle" data-testid="tab-lifecycle">Call Behaviour</TabsTrigger>
        </TabsList>

        {/* ----- BASICS ----- */}
        <TabsContent value="basics" className="mt-6">
          <Section title="Identity">
            <Field label="Name" error={errors.name?.message}>
              <Input
                data-testid="designer-name"
                {...register("name", { required: "Name is required", maxLength: { value: 80, message: "Max 80 chars" } })}
              />
            </Field>
            <Field label="Description" hint="Optional, ≤500 chars">
              <Textarea
                rows={2}
                data-testid="designer-description"
                {...register("description", { maxLength: { value: 500, message: "Max 500 chars" } })}
              />
            </Field>
            <Field label="Language">
              <Controller
                control={control}
                name="language"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="max-w-xs" data-testid="designer-language">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LANGUAGES.map((l) => (
                        <SelectItem key={l.code} value={l.code}>
                          {l.flag} {l.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </Field>
            <Field label="Enabled">
              <Controller
                control={control}
                name="enabled"
                render={({ field }) => (
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      data-testid="designer-enabled"
                    />
                    <span className="text-sm text-muted-foreground">
                      Workers will only fetch enabled agents.
                    </span>
                  </div>
                )}
              />
            </Field>
          </Section>
        </TabsContent>

        {/* ----- BEHAVIOUR ----- */}
        <TabsContent value="behaviour" className="mt-6">
          <Section title="System prompt">
            <Field
              label="System prompt"
              hint={`${watch("system_prompt")?.length || 0} / 8000`}
              error={errors.system_prompt?.message}
            >
              <Textarea
                rows={10}
                className="font-mono text-sm"
                data-testid="designer-system-prompt"
                {...register("system_prompt", {
                  required: "System prompt is required",
                  maxLength: { value: 8000, message: "Max 8000 chars" },
                })}
              />
            </Field>
            <Field label="First message" hint="Optional. Spoken at the start of every call.">
              <Input
                data-testid="designer-first-message"
                {...register("first_message", {
                  maxLength: { value: 1000, message: "Max 1000 chars" },
                })}
              />
            </Field>
          </Section>

          <EndRules control={control} register={register} />
          <QAPairs control={control} register={register} errors={errors} />
        </TabsContent>

        {/* ----- VOICE & AI ----- */}
        <TabsContent value="voice" className="mt-6">
          <Section title="Speech-to-Text">
            <ProviderRow
              label="Provider"
              providers={["deepgram", "assemblyai"]}
              control={control}
              name="stt_provider"
              testid="designer-stt-provider"
            />
            <Field label="Model" hint={`Default: ${STT_MODELS[sttProvider]?.[0]}`}>
              <Controller
                control={control}
                name="stt_model"
                render={({ field }) => (
                  <Select
                    value={field.value || ""}
                    onValueChange={field.onChange}
                  >
                    <SelectTrigger className="max-w-md" data-testid="designer-stt-model">
                      <SelectValue placeholder="(use default)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__default__">(use default)</SelectItem>
                      {(STT_MODELS[sttProvider] || []).map((m) => (
                        <SelectItem key={m} value={m}>
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </Field>
          </Section>

          <Section title="Large Language Model">
            <ProviderRow
              label="Provider"
              providers={["groq", "openai"]}
              control={control}
              name="llm_provider"
              testid="designer-llm-provider"
              onChange={(v) => setValue("llm_model", LLM_MODELS[v][0])}
            />
            <Field label="Model">
              <Controller
                control={control}
                name="llm_model"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="max-w-md" data-testid="designer-llm-model">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(LLM_MODELS[llmProvider] || []).map((m) => (
                        <SelectItem key={m} value={m}>
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </Field>
            <Field
              label="Temperature"
              hint={`Current: ${watch("llm_temperature")?.toFixed(2)} (0 = deterministic, 1.5 = wild)`}
            >
              <Controller
                control={control}
                name="llm_temperature"
                render={({ field }) => (
                  <Slider
                    min={0}
                    max={1.5}
                    step={0.05}
                    value={[field.value]}
                    onValueChange={(v) => field.onChange(v[0])}
                    className="max-w-md"
                    data-testid="designer-temperature"
                  />
                )}
              />
            </Field>
            <Field label="Max tokens" hint="Per response. 50–4000.">
              <Input
                type="number"
                min={50}
                max={4000}
                className="max-w-[200px]"
                data-testid="designer-max-tokens"
                {...register("llm_max_tokens", { valueAsNumber: true })}
              />
            </Field>
          </Section>

          <Section title="Text-to-Speech">
            <ProviderRow
              label="Provider"
              providers={["cartesia", "elevenlabs"]}
              control={control}
              name="tts_provider"
              testid="designer-tts-provider"
              onChange={(v) => setValue("tts_model", TTS_MODELS[v][0])}
            />
            <Field label="Model">
              <Controller
                control={control}
                name="tts_model"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className="max-w-md" data-testid="designer-tts-model">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(TTS_MODELS[ttsProvider] || []).map((m) => (
                        <SelectItem key={m} value={m}>
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </Field>

            <VoicePicker
              provider={ttsProvider}
              language={language}
              control={control}
              setValue={setValue}
              currentVoiceId={watch("tts_voice_id")}
            />
          </Section>
        </TabsContent>

        {/* ----- LIFECYCLE ----- */}
        <TabsContent value="lifecycle" className="mt-6">
          <Section title="Call lifecycle">
            <Field label="Max call duration (seconds)" hint="30 – 3600">
              <Input
                type="number"
                min={30}
                max={3600}
                className="max-w-[200px]"
                data-testid="designer-max-duration"
                {...register("max_call_duration_s", { valueAsNumber: true })}
              />
            </Field>
            <Field label="Silence timeout (seconds)" hint="5 – 120. Hang up after this much silence.">
              <Input
                type="number"
                min={5}
                max={120}
                className="max-w-[200px]"
                data-testid="designer-silence"
                {...register("silence_timeout_s", { valueAsNumber: true })}
              />
            </Field>
            <Field label="Allow barge-in">
              <Controller
                control={control}
                name="allow_barge_in"
                render={({ field }) => (
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      data-testid="designer-barge-in"
                    />
                    <span className="text-sm text-muted-foreground">
                      Caller can interrupt the agent mid-sentence.
                    </span>
                  </div>
                )}
              />
            </Field>
          </Section>
        </TabsContent>
      </Tabs>

      <StickyFooter saving={saving} dirty={isDirty} isEdit={isEdit} agentId={isEdit ? id : null} />
    </form>
  );
}

// ---------------------------------------------------------------------------
// Helper components
// ---------------------------------------------------------------------------
function Section({ title, children }) {
  return (
    <div className="mb-8 rounded-lg border border-border bg-card p-6">
      <h2 className="mb-4 text-base font-semibold">{title}</h2>
      <div className="space-y-5">{children}</div>
    </div>
  );
}

function Field({ label, hint, error, children }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {hint && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}

function ProviderRow({ label, providers, control, name, testid, onChange }) {
  return (
    <Field label={label}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Select
            value={field.value}
            onValueChange={(v) => {
              field.onChange(v);
              onChange?.(v);
            }}
          >
            <SelectTrigger className="max-w-md" data-testid={testid}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {providers.map((p) => (
                <SelectItem key={p} value={p}>
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      />
    </Field>
  );
}

function EndRules({ control, register }) {
  const { fields, append, remove } = useFieldArray({ control, name: "end_rules" });
  return (
    <Section title="End rules">
      <p className="text-xs text-muted-foreground">
        Plain-language conditions that signal the call should end. The LLM uses these as guidance.
      </p>
      <div className="space-y-2" data-testid="designer-end-rules">
        {fields.map((f, i) => (
          <div key={f.id} className="flex gap-2">
            <Input
              placeholder="e.g. Caller says goodbye or thanks."
              {...register(`end_rules.${i}`)}
              data-testid={`designer-rule-${i}`}
            />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => remove(i)}
              data-testid={`designer-rule-remove-${i}`}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        ))}
        {fields.length < 10 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => append("")}
            data-testid="designer-rule-add"
          >
            <Plus className="h-4 w-4" /> Add rule
          </Button>
        )}
      </div>
    </Section>
  );
}

function QAPairs({ control, register, errors }) {
  const { fields, append, remove } = useFieldArray({ control, name: "qa_pairs" });
  return (
    <Section title="Reference Q&A">
      <p className="text-xs text-muted-foreground">
        Optional question/answer pairs the agent should know. Up to 50.
      </p>
      <div className="space-y-3" data-testid="designer-qa-pairs">
        {fields.map((f, i) => (
          <div
            key={f.id}
            className="grid grid-cols-[1fr_1fr_auto] gap-2 items-start"
          >
            <Input
              placeholder="Question"
              {...register(`qa_pairs.${i}.q`, { required: true, maxLength: 500 })}
              data-testid={`designer-qa-q-${i}`}
            />
            <Input
              placeholder="Answer"
              {...register(`qa_pairs.${i}.a`, { required: true, maxLength: 500 })}
              data-testid={`designer-qa-a-${i}`}
            />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => remove(i)}
              data-testid={`designer-qa-remove-${i}`}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        ))}
        {fields.length < 50 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => append({ q: "", a: "" })}
            data-testid="designer-qa-add"
          >
            <Plus className="h-4 w-4" /> Add Q&A
          </Button>
        )}
      </div>
    </Section>
  );
}

function VoicePicker({ provider, language, control, setValue, currentVoiceId }) {
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/voices", {
          params: { provider, language },
        });
        if (!cancel) setVoices(res.data.voices || []);
      } catch (err) {
        if (!cancel) toastError(err, "Could not load voices");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [provider, language]);

  return (
    <Field
      label="Voice"
      hint={`${voices.length} voices available for ${language.toUpperCase()} on ${provider}`}
    >
      <Controller
        control={control}
        name="tts_voice_id"
        rules={{ required: "Voice is required" }}
        render={({ field, fieldState }) => (
          <div data-testid="designer-voice-picker">
            <Input
              placeholder="paste a voice ID or pick from below"
              value={field.value || ""}
              onChange={(e) => field.onChange(e.target.value)}
              className="max-w-md font-mono text-xs mb-3"
              data-testid="designer-voice-id"
            />
            {fieldState.error && (
              <p className="text-xs text-destructive mb-2">
                {fieldState.error.message}
              </p>
            )}
            {loading ? (
              <div className="text-xs text-muted-foreground">
                <Loader2 className="inline h-3 w-3 animate-spin mr-1" />
                Loading voices…
              </div>
            ) : (
              <div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
                data-testid="designer-voice-grid"
              >
                {voices.map((v) => {
                  const selected = v.id === currentVoiceId;
                  return (
                    <button
                      key={v.id + v.name}
                      type="button"
                      onClick={() => setValue("tts_voice_id", v.id, { shouldDirty: true })}
                      className={cn(
                        "rounded-md border p-3 text-left transition hover:border-primary",
                        selected
                          ? "border-primary bg-primary/5 ring-1 ring-primary"
                          : "border-border bg-card"
                      )}
                      data-testid={`designer-voice-${v.id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-sm">{v.name}</span>
                        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
                          {v.gender}
                        </span>
                      </div>
                      <p className="text-xs italic text-muted-foreground line-clamp-2">
                        "{v.preview_text}"
                      </p>
                      <p className="mt-2 font-mono text-[10px] text-muted-foreground/60 truncate">
                        {v.id}
                      </p>
                    </button>
                  );
                })}
                {voices.length === 0 && (
                  <p className="text-xs text-muted-foreground col-span-full">
                    No catalogued voices for this provider/language. You can
                    still paste a voice ID in the field above.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      />
    </Field>
  );
}

function StickyFooter({ saving, dirty, isEdit, agentId }) {
  const [validating, setValidating] = useState(false);
  const [validationOpen, setValidationOpen] = useState(false);
  const [result, setResult] = useState(null);

  const onValidate = async () => {
    if (!agentId) return;
    setValidating(true);
    try {
      const r = await api.post(`/agents/${agentId}/validate-against-worker`);
      setResult(r.data);
      setValidationOpen(true);
    } catch (err) {
      toastError(err, "Validation failed");
    } finally {
      setValidating(false);
    }
  };

  return (
    <>
      <div
        className="fixed bottom-0 left-60 right-0 border-t border-border bg-card/90 backdrop-blur p-3 pr-48 flex items-center justify-end gap-3"
        data-testid="designer-footer"
      >
        <span className="text-xs text-muted-foreground mr-auto">
          {dirty ? "Unsaved changes" : "All saved"}
        </span>
        {isEdit && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onValidate}
            disabled={validating || dirty}
            data-testid="designer-validate"
            title={dirty ? "Save first to validate the persisted version" : "Round-trip the saved doc through the worker's AgentConfig"}
          >
            {validating
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <ShieldCheck className="h-4 w-4" />}
            Validate against worker
          </Button>
        )}
        <Button type="submit" disabled={saving || !dirty} data-testid="designer-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {isEdit ? "Save changes" : "Create agent"}
        </Button>
      </div>

      <Dialog open={validationOpen} onOpenChange={setValidationOpen}>
        <DialogContent data-testid="designer-validate-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {result?.ok
                ? <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                : <XCircle className="h-5 w-5 text-destructive" />}
              {result?.ok
                ? "Worker accepts this agent"
                : "Worker would reject this agent"}
            </DialogTitle>
            <DialogDescription>
              The saved doc was round-tripped through the voice worker's
              <code className="font-mono"> AgentConfig</code> Pydantic model.
            </DialogDescription>
          </DialogHeader>
          {result?.errors?.length > 0 && (
            <div className="rounded-md border border-destructive bg-destructive/5 p-3 max-h-64 overflow-auto">
              <div className="text-xs font-semibold uppercase mb-2 text-destructive">Errors</div>
              <ul className="space-y-1 text-xs font-mono break-all">
                {result.errors.map((e, i) => (
                  <li key={i} data-testid={`designer-validate-err-${i}`}>{e}</li>
                ))}
              </ul>
            </div>
          )}
          {result?.fields_dropped?.length > 0 && (
            <div className="text-xs text-muted-foreground">
              <b>Admin-only fields the worker ignores:</b>{" "}
              <code className="font-mono">{result.fields_dropped.join(", ")}</code>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setValidationOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
